import axios from "axios";

const PROD_API = "https://veltaone.it/api";
/** HTTP sull'IP: HTTPS :443 di veltaone.it / api.veltaone.it non risponde. */
const NATIVE_API = "http://2.29.16.27/api";
const HTTP_APK_URL = "http://2.29.16.27/downloads/veltaone-latest.apk";

export function reachableApkUrl(url) {
  const u = String(url || "").trim();
  if (!u) return HTTP_APK_URL;
  const low = u.toLowerCase();
  if (low.startsWith("https://api.veltaone.it") || low.startsWith("https://veltaone.it") || low.startsWith("https://www.veltaone.it")) {
    return HTTP_APK_URL;
  }
  return u;
}

const stripSlash = (u) => String(u || "").trim().replace(/\/+$/, "");

const isLoopbackHost = (host) => {
  const h = String(host || "").toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0" || h === "::1" || h === "[::1]";
};

const isLocalhostUrl = (u) => {
  try {
    return isLoopbackHost(new URL(u).hostname) || new URL(u).hostname === "10.0.2.2";
  } catch {
    return /localhost|127\.0\.0\.1|10\.0\.2\.2/i.test(u);
  }
};

/** Accetta sia https://host sia https://host/api e restituisce origine + base /api. */
const splitOriginAndApi = (url) => {
  const raw = stripSlash(url);
  if (!raw) return { origin: "", apiBase: "" };
  if (/\/api$/i.test(raw)) {
    return { origin: stripSlash(raw.replace(/\/api$/i, "")), apiBase: raw };
  }
  return { origin: raw, apiBase: `${raw}/api` };
};

const pageHostname = () => {
  try {
    return typeof window !== "undefined" ? window.location.hostname : "";
  } catch {
    return "";
  }
};

/** In LAN (tablet → PC) sostituisce localhost con l'IP della pagina. */
const rewriteLoopbackToLan = (url) => {
  const host = pageHostname();
  if (!host || isLoopbackHost(host) || host === "10.0.2.2") return url;
  try {
    const u = new URL(url);
    if (isLoopbackHost(u.hostname)) {
      u.hostname = host;
      return stripSlash(u.toString());
    }
  } catch {
    /* ignore */
  }
  return url;
};

/**
 * Produzione su veltaone.it: stesso host `/api` (nginx → container API).
 * APK Capacitor: hostname = localhost, quindi NON usare `/api` relativo.
 * `https://api.veltaone.it` non è in ascolto → "Server non raggiungibile".
 */
function isNativeApp() {
  try {
    const C = typeof window !== "undefined" ? window.Capacitor : null;
    if (C && typeof C.isNativePlatform === "function" && C.isNativePlatform()) return true;
  } catch {
    /* ignore */
  }
  const host = pageHostname();
  if (host === "localhost" || host === "127.0.0.1") return true;
  try {
    const p = typeof window !== "undefined" ? window.location.protocol : "";
    if (p === "capacitor:" || p === "ionic:") return true;
  } catch {
    /* ignore */
  }
  return false;
}

export function resolveBackendUrl() {
  try {
    const override = localStorage.getItem("velta_api_base");
    if (override && /^https?:\/\//i.test(override.trim())) return stripSlash(override);
  } catch {
    /* ignore */
  }

  const host = pageHostname();
  if (host && /(^|\.)veltaone\.it$/i.test(host)) {
    return "/api";
  }
  // Stesso nginx del sito: HTTP sull'IP (QZ Tray). Mai https://veltaone.it da questa origine (CORS + toast rosso).
  if (host === "2.29.16.27" || host === "www.veltaone.it") {
    return "/api";
  }
  if (isNativeApp()) {
    return NATIVE_API;
  }

  const fromEnv = stripSlash(process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "");
  const invalid = !fromEnv || fromEnv === "undefined" || /undefined/i.test(fromEnv);
  const isProd = process.env.NODE_ENV === "production";

  if (invalid) {
    return "/api";
  }

  if (isProd) {
    if (!isLocalhostUrl(fromEnv) && /^https:\/\//i.test(fromEnv) && !/api\.veltaone\.it/i.test(fromEnv)) return fromEnv;
    if (fromEnv === "/api" || fromEnv.endsWith("/api")) return fromEnv.startsWith("http") ? fromEnv : "/api";
    return stripSlash(process.env.REACT_APP_PROD_API_URL || PROD_API);
  }

  if (fromEnv === "/api") return "/api";
  const withScheme = /^https?:\/\//i.test(fromEnv) ? fromEnv : `http://${fromEnv}`;
  return rewriteLoopbackToLan(withScheme);
}

const resolved = splitOriginAndApi(resolveBackendUrl());
const rawApiBase = resolved.apiBase;
export const BACKEND_URL = resolved.origin && resolved.origin !== "undefined" ? resolved.origin : "";
export const API_BASE = (!rawApiBase || rawApiBase.includes("undefined")) ? "/api" : rawApiBase;

/** URL API sicuro: mai `undefined/api/...`. Path relativo a `/api`. */
export function apiUrl(path = "") {
  const p = String(path || "");
  const suffix = p.startsWith("/") ? p : `/${p}`;
  const { origin, apiBase } = splitOriginAndApi(resolveBackendUrl());
  if (suffix.startsWith("/api/") || suffix === "/api") {
    if (origin && /^https?:\/\//i.test(origin)) return `${origin}${suffix}`;
    return suffix;
  }
  const base = apiBase || "/api";
  return `${base}${suffix}`;
}

export const isNetworkError = (err) => {
  if (!err) return false;
  if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return false;
  if (err.response) return false;
  const msg = String(err.message || "");
  return (
    err.code === "ERR_NETWORK" ||
    err.code === "ECONNABORTED" ||
    err.code === "ERR_CONNECTION_REFUSED" ||
    /network error/i.test(msg) ||
    /failed to fetch/i.test(msg) ||
    /timeout/i.test(msg)
  );
};

let lastNetworkToastAt = 0;
const notifyNetworkError = () => {
  const now = Date.now();
  if (now - lastNetworkToastAt < 4000) return;
  lastNetworkToastAt = now;
  import("sonner")
    .then(({ toast }) => {
      toast.error("Server non raggiungibile. Controlla la connessione e riprova.");
    })
    .catch(() => {});
};

const CATALOG_GET = /^\/(products|categories|printers)(\?|$)/;

function catalogCacheKey(config) {
  const url = String(config?.url || "");
  if (!CATALOG_GET.test(url)) return "";
  const method = String(config?.method || "get").toLowerCase();
  if (method !== "get") return "";
  const u = getCurrentUser();
  const tid = u?.tenant_id || u?.tenantId || "";
  if (!tid) return "";
  return `velta_catalog:${tid}:${url}`;
}

function rememberCatalog(config, data) {
  const key = catalogCacheKey(config);
  if (!key || data == null) return;
  try {
    const raw = JSON.stringify(data);
    if (raw.length > 1500000) return;
    localStorage.setItem(key, raw);
  } catch { /* ignore */ }
}

function readCatalog(config) {
  const key = catalogCacheKey(config);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener(
    "unhandledrejection",
    (event) => {
      const reason = event.reason;
      if (isNetworkError(reason) || reason?.isAxiosError && !reason?.response) {
        event.preventDefault();
        if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
      }
    },
    true
  );
}

export const api = axios.create({
  baseURL: API_BASE || "/api",
  timeout: 25000,
});

api.interceptors.request.use((config) => {
  const { apiBase } = splitOriginAndApi(resolveBackendUrl());
  config.baseURL = apiBase || "/api";
  if (!config.baseURL || String(config.baseURL).includes("undefined")) {
    config.baseURL = "/api";
  }
  if (typeof config.url === "string" && config.url.includes("undefined")) {
    config.url = config.url.replace(/^.*undefined\/api/, "").replace(/^undefined/, "");
  }
  const token = localStorage.getItem("velta_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => {
    rememberCatalog(r.config, r.data);
    return r;
  },
  (err) => {
    if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
      return Promise.reject(err);
    }

    if (isNetworkError(err) || (err?.isAxiosError && !err?.response)) {
      const cached = readCatalog(err.config);
      if (cached != null) {
        return Promise.resolve({ data: cached, status: 200, statusText: "OK", headers: {}, config: err.config, cachedOffline: true });
      }
      err.isNetworkError = true;
      err.userMessage = "Server non raggiungibile. Controlla la connessione e riprova.";
      if (!err.config?.skipNetworkToast) notifyNetworkError();
      return Promise.reject(err);
    }

    if (err?.response?.status === 401) {
      localStorage.removeItem("velta_token");
      localStorage.removeItem("velta_user");
      const path = window.location.pathname || "";
      if (!path.startsWith("/login") && !path.startsWith("/attiva") && path !== "/") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const formatEUR = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));

export const firstCategoryWithProducts = (cats, prods) => {
  const catList = Array.isArray(cats) ? cats : [];
  const prodList = Array.isArray(prods) ? prods : [];
  const withProds = catList.find((c) => prodList.some((p) => p.category_id === c.id && p.visible !== false));
  return (withProds || catList[0])?.id ?? null;
};

export const formatApiError = (err, fallback = "Errore") => {
  if (isNetworkError(err) || err?.isNetworkError) {
    return err.userMessage || "Server non raggiungibile. Controlla la connessione e riprova.";
  }
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join("; ");
  return JSON.stringify(d);
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export const getCurrentUser = () => {
  try { return JSON.parse(localStorage.getItem("velta_user") || "null"); }
  catch { return null; }
};

export const hasPerm = (perm) => {
  const u = getCurrentUser();
  if (!u) return false;
  if (u.role === "client_owner" || u.role === "client" || u.role === "superadmin" || u.is_demo) return true;
  const perms = Array.isArray(u.permissions) ? u.permissions : [];
  if (perm === "prezzi_acquisto" || perm === "can_view_purchase_prices") {
    return perms.includes("prezzi_acquisto") || perms.includes("can_view_purchase_prices");
  }
  return perms.includes(perm);
};

export const canViewPurchasePrices = () => hasPerm("can_view_purchase_prices");

export const isOwner = () => {
  const u = getCurrentUser();
  return u?.role === "client_owner" || u?.role === "client" || !!u?.is_demo;
};

export const isModeAllowed = (mode) => {
  const u = getCurrentUser();
  if (!u) return false;
  if (u.role === "client_owner" || u.role === "client" || u.is_demo) return true;
  const allowed = u.allowed_modes;
  if (allowed == null) return true;
  return (Array.isArray(allowed) ? allowed : []).includes(mode);
};
